import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
  ElementRef,
  ViewChild,
  Inject,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder, Validators,
} from '@angular/forms';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocomplete as MatAutocomplete,
} from '@angular/material/legacy-autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BehaviorSubject, Observable,
  of,
  Subject,
} from 'rxjs';
import {
  map, catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
  finalize,
  takeUntil,
} from 'rxjs/operators';

import { IUserAccountInterface } from '../../../client/interfaces/client-company.interface';
import {
  ApprovalRuleApiService,
  ApprovalModelApiService,
  ApprovalUserTemplateApiService, SnackBarConfig,
} from '../../../shared';
import {
  trackByValue,
  trackByControlValue,
  trackById,
  trackByLabel,
} from '../../../shared/util';
import { emptyStringValidator } from '../../../shared/util/custom-validation.utils';
import {
  IApprovalRule, IApprovalUserTemplate,
  IApprovalUserTemplateAssignment,
  ELogicalOperators,
  EComparisonOperators,
} from '../../interfaces/approval.interface';
import {
  atLeastOneCompleteConditionValidator,
  completeRowConditionValidator,
} from '../../validators/approval-rule-form-validations';

interface ComparisonOperator {
  value :string;
  display :string;
}

@Component({
  selector: 'lum-approval-rule-form',
  templateUrl: './approval-rule-form.component.html',
  styleUrls: ['./approval-rule-form.component.scss'],
})
export class ApprovalRuleFormComponent implements OnInit, OnDestroy {
  @ViewChild('autocompleteInput')
  autocompleteInput :ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  auto :MatAutocomplete;

  approvalRuleId :number = 0;

  approvalRule :IApprovalRule;

  submittingForm :boolean = false;
  loading :boolean = false;

  form :UntypedFormGroup;

  selectedUsers :IUserAccountInterface[] = [];

  approvalUserTemplates :IApprovalUserTemplate[] = [];
  inputValue :string = '';
  private searchUpdated$ = new BehaviorSubject<string>('');
  private readonly _approvalUserTemplateLoading = new BehaviorSubject<boolean>(
    <boolean>false,
  );
  approvalUserTemplateLoading$ = this._approvalUserTemplateLoading.asObservable();

  trackByLabel = trackByLabel;
  trackByValue = trackByValue;
  trackById = trackById;
  trackByControlValue = trackByControlValue;

  allModels :{ value :string | null; label :string }[] = [];
  allModelFields :{ [key :string] :any[] };

  selectedFields :any[] = [];

  logicalOperators :{ value :string | null; label :string }[];
  comparisonOperators :{ value :string | null; label :string }[];

  displayApprovalUserTemplateControl = new UntypedFormControl();

  selectedFieldValues :any[] = [];

  private onDestroy$ = new Subject();

  constructor(
    private approvalModelApiService :ApprovalModelApiService,
    private approvalUserTemplateApiService :ApprovalUserTemplateApiService,
    private approvalRuleApiService :ApprovalRuleApiService,
    private route :ActivatedRoute,
    private router :Router,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    @Optional() public dialogRef :MatDialogRef<ApprovalRuleFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data :IApprovalRule,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initComponentInputs();
    this.onSearchUpdate();
    this.getRouterParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  updateSelectedFieldValue(index :number, fieldValue :string) :void {
    const selectedField = this.selectedFields.find((f) => f.name === fieldValue);
    this.selectedFieldValues[index] = selectedField;
  }

  drop(event :CdkDragDrop<string[]>) :void {
    const { previousIndex, currentIndex } = event;
    const control = this.conditionsFormArray.at(previousIndex);
    this.conditionsFormArray.removeAt(previousIndex);
    this.conditionsFormArray.insert(currentIndex, control);
  }

  markFormArrayAsTouched(formArray :UntypedFormArray) :void {
    formArray.controls.forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  markFormGroupAsTouched(formGroup :UntypedFormGroup) :void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  onSubmit() :void {
    this.form.markAllAsTouched();

    if (this.form.get('condition')?.errors?.['noConditionsAdded']) {
      this.snackBar.open('You must add at least one condition.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (
      this.form.get('condition')?.errors?.['atLeastOneCompleteConditionRequired']
    ) {
      this.snackBar.open('At least one complete condition is required.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (this.form.get('condition')?.errors?.['incompleteCondition']) {
      this.snackBar.open(
        'Please complete all condition rows or clear the incomplete ones.',
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;
    }

    if (this.form.invalid) {
      return;
    }

    const conditions = this.conditionsFormArray.controls
      .map((control) => control.value)
      .filter(({ field, operator, value }) => field || operator || value)
      .map((condition, index) => ({ ...condition, order: index }));

    this.submittingForm = true;

    try {
      if (!this.approvalRuleId) {
        this.approvalRuleApiService
          .create({
            name: this.form.value.name,
            model: this.form.value.model,
            operator: this.form.value.operator,
            approvalUserTemplateId: this.form.value.approvalUserTemplateId,
            enableSingleApprover: this.form.value.enableSingleApprover,
            condition: conditions,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open('Approval Rule successfully added.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['settings/approval-rule-settings']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      } else {
        this.approvalRuleApiService
          .update(this.approvalRuleId, {
            name: this.form.value.name,
            model: this.form.value.model,
            operator: this.form.value.operator,
            approvalUserTemplateId: this.form.value.approvalUserTemplateId,
            enableSingleApprover: this.form.value.enableSingleApprover,
            condition: conditions,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open('Approval Rule successfully updated.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['settings/approval-rule-settings']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      }
    } catch (error :any) {
      this.submittingForm = false;
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      name: [null, [Validators.required, emptyStringValidator()]],
      model: [null, [Validators.required]],
      operator: [null, [Validators.required]],
      approvalUserTemplateId: [null, [Validators.required]],
      condition: this.fb.array(
        [],
        [atLeastOneCompleteConditionValidator(), completeRowConditionValidator()],
      ),
      enableSingleApprover: [false],
    });

    if (!this.approvalRuleId) {
      this.addBlankConditionRow();
    }

    this.logicalOperators = Object.entries(ELogicalOperators).map(
      ([label, value]) => {
        return { value, label };
      },
    );

    this.comparisonOperators = Object.entries(EComparisonOperators).map(
      ([label, value]) => {
        return {
          value: value,
          label: this.getDisplayTextForComparisonOperator(value),
        };
      },
    );
  }

  private getDisplayTextForComparisonOperator(
    operator :EComparisonOperators,
  ) :string {
    switch (operator) {
      case EComparisonOperators.GREATER_THAN:
        return 'Greater than';
      case EComparisonOperators.LESS_THAN:
        return 'Less than';
      case EComparisonOperators.GREATER_THAN_OR_EQUAL_TO:
        return 'Greater than or equal to';
      case EComparisonOperators.LESS_THAN_OR_EQUAL_TO:
        return 'Less than or equal to';
      case EComparisonOperators.EQUAL_TO:
        return 'Equal to';
      case EComparisonOperators.NOT_EQUAL_TO:
        return 'Not equal to';
      default:
        return '';
    }
  }

  private addBlankConditionRow() :void {
    const conditionGroup = this.fb.group({
      field: [null],
      operator: [null],
      value: [null],
    });

    this.conditionsFormArray.push(conditionGroup);
  }

  private setFormValue() :void {
    if (this.approvalRule && this.approvalRule.id) {
      this.form?.get('name')?.setValue(this.approvalRule.name);
      this.form?.get('operator')?.setValue(this.approvalRule.operator);
      this.form?.get('model')?.setValue(this.approvalRule.model);
      this.form
        ?.get('approvalUserTemplateId')
        ?.setValue(this.approvalRule.approvalUserTemplateId);
      this.form?.get('enableSingleApprover')?.setValue(
        this.approvalRule.enableSingleApprover ?? false,
      );

      if (this.approvalRule.condition) {
        const conditions = this.approvalRule.condition;
        const conditionFormArray = this.form.get('condition') as UntypedFormArray;

        while (conditionFormArray.length !== 0) {
          conditionFormArray.removeAt(0);
        }

        conditions.forEach((condition :any) => {
          const conditionGroup = this.fb.group({
            field: [condition.field],
            operator: [condition.operator],
            value: [condition.value],
          });

          const fieldControl = conditionGroup.get('field');
          if (fieldControl !== null) {
            fieldControl.valueChanges.subscribe((field) => {
              this.updateValueFormControl(conditionGroup, field);
            });
          }

          conditionFormArray.push(conditionGroup);
        });
      }

      if (this.approvalRule.approvalUserTemplate) {
        this.displayApprovalUserTemplateControl.setValue(
          this.approvalRule.approvalUserTemplate.label,
        );
        this.approvalUserTemplates.push(this.approvalRule.approvalUserTemplate);
      }

      this.selectedUsers =
        this.approvalRule.approvalUserTemplate?.approvalUserTemplateAssignments
          ?.map((assignment :IApprovalUserTemplateAssignment) => assignment.user)
          .filter((user) :user is IUserAccountInterface => user !== undefined) ??
        [];

      this.loadModels();
      this.cd.detectChanges();
    }
  }

  private loadData(id :number) :void {
    this.loading = true;
    this.approvalRuleApiService
      .getById(id, {
        relations: 'approvalUserTemplate.approvalUserTemplateAssignments.user',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.approvalRule = response;
          this.setFormValue();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }

  private loadModels() :void {
    this.loading = true;
    this.approvalModelApiService
      .getAllModelFields()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;

          if (response && typeof response === 'object') {
            this.allModelFields = response;

            if (this.approvalRule?.model) {
              this.selectedFields =
                this.allModelFields[this.approvalRule.model] || [];

              const conditions = this.approvalRule.condition;
              conditions.forEach((condition :any, index :number) => {
                const selectedField = this.selectedFields.find(
                  (f) => f.name === condition.field,
                );
                this.selectedFieldValues[index] = selectedField;
              });
            }

            Object.keys(response).forEach((key) => {
              const value = key;
              const label = key
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

              this.allModels.push({ value, label });
            });
          }
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }

  onModelSelect(model :string) {
    this.selectedFields = this.allModelFields[model] || [];
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { approvalRuleId } = params;
        if (approvalRuleId) {
          this.approvalRuleId = approvalRuleId;
          this.loadData(this.approvalRuleId);
        }
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onSearchKeywordChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  private onSearchUpdate() :void {
    this._approvalUserTemplateLoading.next(true);

    this.searchUpdated$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchValue) =>
          this.approvalUserTemplateApiService
            .get({
              label: searchValue,
              relations: 'approvalUserTemplateAssignments.user',
            })
            .pipe(
              catchError((error) => {
                this.snackBar.open(
                  error?.error ? error.error : 'Error searching record',
                  'Close',
                  { duration: SnackBarConfig.DEFAULT.duration },
                );

                return of(this.approvalUserTemplates);
              }),
            ),
        ),
        map((response) => response || []),
        finalize(() => this._approvalUserTemplateLoading.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe((response) => {
        this.approvalUserTemplates = response.data;
        this._approvalUserTemplateLoading.next(false);
      });
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    const selectedTemplateId :number = event.option.value;

    const selectedTemplate = this.approvalUserTemplates.find(
      (template) => template.id === selectedTemplateId,
    );

    this.form.get('approvalUserTemplateId')?.setValue(selectedTemplateId);

    this.displayApprovalUserTemplateControl.setValue(selectedTemplate?.label);

    this.selectedUsers =
      selectedTemplate?.approvalUserTemplateAssignments
        ?.map((assignment :IApprovalUserTemplateAssignment) => assignment.user)
        .filter((user) :user is IUserAccountInterface => user !== undefined) ?? [];
  }

  private initComponentInputs() :void {
    if (this.isDialog && this.data) {
      this.approvalRule = this.data;
      this.approvalRuleId = this.data.id;
      this.setFormValue();
      this.cd.detectChanges();
    } else {
      this.loadModels();
    }
  }

  onRemoveCondition(i :number) :void {
    this.conditionsFormArray.removeAt(i);
  }

  onAddCondition(condition ?:UntypedFormGroup) :void {
    if (!condition) {
      const conditionGroup = this.fb.group({
        field: [null],
        operator: [null],
        value: [null],
      });

      const fieldControl = conditionGroup.get('field');
      if (fieldControl !== null) {
        fieldControl.valueChanges.subscribe((field) => {
          this.updateValueFormControl(conditionGroup, field);
        });
      }

      this.conditionsFormArray.push(conditionGroup);
    } else {
      this.conditionsFormArray.push(condition);
    }
  }

  updateValueFormControl(
    conditionGroup :UntypedFormGroup,
    fieldName :string,
  ) :void {
    const selectedField = this.selectedFields.find(
      (field) => field.name === fieldName,
    );

    if (selectedField) {
      switch (selectedField.inputType) {
        case 'number':
          conditionGroup.setControl(
            'value',
            this.fb.control(null, Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)),
          );
          break;
        case 'select':
          conditionGroup.setControl('value', this.fb.control(null));
          break;
        case 'checkbox':
          conditionGroup.setControl('value', this.fb.control(false));
          break;
        default:
          conditionGroup.setControl('value', this.fb.control(null));
          break;
      }
    }
  }

  onAddMultipleConditions(rows :number) :void {
    for (let condition = 0; condition < rows; condition++) {
      this.onAddCondition();
    }
  }

  get conditionsFormArray() :UntypedFormArray {
    return this.form.get('condition') as UntypedFormArray;
  }

  get isDialog() :boolean {
    return this.dialogRef?.componentInstance === this;
  }

  get selectedFieldInputType() :string {
    const selectedFieldName = this.form.get('field')?.value;
    const selectedField = this.selectedFields.find(
      (field) => field.name === selectedFieldName,
    );

    return selectedField ? selectedField.inputType : 'text'; // Default to 'text' if not found
  }
}
